import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart, formatTL } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/SEO";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1643822308521-1da534425d82?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("hepsi");
  const { add } = useCart();
  const { user } = useAuth();
  const isDealer = user && user.role === "dealer";

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  const categories = ["hepsi", ...Array.from(new Set(products.map((p) => p.category).filter(Boolean)))];
  const filtered = filter === "hepsi" ? products : products.filter((p) => p.category === filter);

  const handleAdd = (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    add(p);
    toast.success(`${p.name} sepete eklendi`);
  };

  return (
    <>
      <SEO title="Ürünler — Akrilik Hobi Boyaları" description="Wünberg premium akrilik hobi boyalarının renk paleti, hacimler ve özellikler." />
      <section className="pt-32 pb-12 border-b border-white/10">
        <div className="wb-container">
          <div className="wb-overline">Katalog</div>
          <h1 className="wb-h1 mt-3">Ürünler</h1>
          <p className="wb-body mt-5 max-w-2xl">
            Sanatçıların seçtiği renkler.
            {isDealer && <span className="block mt-2 text-[#d4af37]">Bayi indiriminiz: %{user.discount_percent || 0}</span>}
          </p>
        </div>
      </section>

      <section className="wb-section">
        <div className="wb-container">
          {categories.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`filter-${c}`}
                  className={`px-5 py-2 text-xs uppercase tracking-widest border transition-all ${
                    filter === c
                      ? "bg-[#d4af37] text-[#030f26] border-[#d4af37]"
                      : "border-white/20 text-[#a0aabf] hover:border-[#d4af37] hover:text-[#d4af37]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="text-[#a0aabf]">Yükleniyor...</div>
          ) : filtered.length === 0 ? (
            <div className="wb-glass p-16 text-center">
              <div className="font-serif text-2xl">Henüz ürün eklenmedi</div>
              <p className="text-[#a0aabf] mt-3">Admin panelinden ürün eklediğinizde burada görünecek.</p>
              <Link to="/iletisim" className="wb-btn-secondary mt-6 inline-flex">Bize ulaşın <ArrowRight size={16} /></Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => {
                const hasPrice = Number(p.price) > 0;
                return (
                  <div key={p.id} className="wb-card group flex flex-col" data-testid={`product-${p.slug}`}>
                    <Link to={`/urunler/${p.slug}`} className="block">
                      <div className="aspect-[4/5] overflow-hidden bg-[#0c1f44]">
                        <img src={p.image_url || FALLBACK_IMG} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      </div>
                    </Link>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="wb-overline">{p.category}</div>
                      <Link to={`/urunler/${p.slug}`} className="font-serif text-xl mt-2 hover:text-[#d4af37]">{p.name}</Link>
                      <div className="text-sm text-[#a0aabf] mt-2 line-clamp-2 flex-1">{p.short_description}</div>

                      <div className="mt-4 flex items-end justify-between gap-2">
                        <div>
                          {hasPrice ? (
                            <>
                              {isDealer && p.dealer_price != null && (
                                <div className="text-xs text-[#a0aabf] line-through">{formatTL(p.price)}</div>
                              )}
                              <div className="font-serif text-xl text-[#d4af37]">
                                {formatTL(isDealer && p.dealer_price != null ? p.dealer_price : p.price)}
                              </div>
                              <div className="text-[10px] uppercase tracking-widest text-[#a0aabf]">{p.volume} · KDV dahil</div>
                            </>
                          ) : (
                            <>
                              <div className="text-sm text-[#a0aabf]">Fiyat için iletişim</div>
                              <div className="text-[10px] uppercase tracking-widest text-[#a0aabf]">{p.volume}</div>
                            </>
                          )}
                        </div>
                        {hasPrice ? (
                          <button
                            onClick={(e) => handleAdd(e, p)}
                            className="p-2.5 border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#030f26] transition-colors"
                            data-testid={`add-${p.slug}`}
                            title="Sepete ekle"
                          >
                            <ShoppingBag size={16} />
                          </button>
                        ) : (
                          <Link to="/iletisim" className="text-xs text-[#d4af37] hover:underline">İletişim</Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
